import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch } from './redux';
import { logout } from '../store/actions/user.actions';

const DEFAULT_INACTIVITY_MINUTES = 60;
const REMEMBER_KEY = 'remember-device';
const TIMEOUT_KEY = 'inactivity-timeout-minutes'; // set by admin
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

function getTimeoutMs(): number {
  const stored = localStorage.getItem(TIMEOUT_KEY);
  const minutes = stored ? parseInt(stored, 10) : DEFAULT_INACTIVITY_MINUTES;
  return (isNaN(minutes) || minutes < 1 ? DEFAULT_INACTIVITY_MINUTES : minutes) * 60 * 1000;
}

export function useInactivityLogout(isLoggedIn: boolean) {
  const dispatch = useAppDispatch();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isRemembered = () => localStorage.getItem(REMEMBER_KEY) === 'true';

  const resetTimer = useCallback(() => {
    if (!isLoggedIn || isRemembered()) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      dispatch(logout());
    }, getTimeoutMs());
  }, [isLoggedIn, dispatch]);

  useEffect(() => {
    if (!isLoggedIn || isRemembered()) return;

    resetTimer();

    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    return () => {
      if (timer.current) clearTimeout(timer.current);
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [isLoggedIn, resetTimer]);
}

export function setRememberDevice(value: boolean) {
  if (value) {
    localStorage.setItem(REMEMBER_KEY, 'true');
  } else {
    localStorage.removeItem(REMEMBER_KEY);
  }
}

export function getRememberDevice(): boolean {
  return localStorage.getItem(REMEMBER_KEY) === 'true';
}

/** Called by admin settings to change the inactivity timeout for all users on this device. */
export function setInactivityTimeout(minutes: number) {
  localStorage.setItem(TIMEOUT_KEY, String(minutes));
}

export function getInactivityTimeout(): number {
  const stored = localStorage.getItem(TIMEOUT_KEY);
  const n = stored ? parseInt(stored, 10) : DEFAULT_INACTIVITY_MINUTES;
  return isNaN(n) || n < 1 ? DEFAULT_INACTIVITY_MINUTES : n;
}
